import { useCapability } from '../../../hooks/use-capability';

export type AdversarialMode = 'none' | 'soft' | 'strict' | 'competitive';

/**
 * Motor Neutro B4c-b — a sessão RESTRITA (caucus) da conversa, já resolvida PELO SERVIDOR para
 * quem pediu. `compartment: null` = está em caucus e este operador **não** tem direito ao conteúdo.
 *
 * O código é OPACO (cunhado pelo vínculo, não um valor de enum) e a UI o renderiza **verbatim**.
 */
export interface CaucusContext {
  readonly compartment: string | null;
}

export interface AdversarialContext {
  /** Modo adversarial do Bond — ADR-040 (4 valores canônicos sem prefix). */
  adversarialMode: AdversarialMode;
  /**
   * Motor Neutro B4c-b — sessão RESTRITA; `null` = a conversa não está em caucus.
   *
   * SUBSTITUI o antigo `sessionContext`, que lia `bond.sessionContext` — um caminho que nunca
   * existiu no contrato (a sessão é da `Conversation`, não do `Bond`). Manter os dois deixaria
   * duas fontes para a mesma pergunta, e uma delas morta.
   */
  caucus: CaucusContext | null;
}

export interface AdversarialIndicatorResult {
  /** Badge deve renderizar — true quando há contexto adversarial E capability presente. */
  readonly isAdversarial: boolean;
  /** Modo adversarial efetivo — `null` quando não há bond ou capability ausente. */
  readonly mode: Exclude<AdversarialMode, 'none'> | null;
  /** Sessão restrita (caucus) — `null` quando não disponível. */
  readonly caucus: CaucusContext | null;
  /** Capability presente — útil para diagnóstico em UI auditável. */
  readonly hasCapability: boolean;
}

/**
 * useAdversarialIndicator — PR-B Fatia 7 (ADR-040 + Caderno cap 17 + Brief 2A V2.3 adendo terceiro).
 *
 * Retorna o estado consolidado para o `AdversarialBadge` no Composer.
 *
 * **Comportamento canônico:**
 *   - `adversarial === null` (conversa sem bond — caso comum NetComet) → oculto.
 *   - `adversarialMode === 'none'` → oculto.
 *   - Capability `ai-context-adversarial-filter` ausente → oculto (defense-in-depth
 *     já existe no Engine via filtro de contexto; capability gate aqui é
 *     apenas para UI; cf. ADR-040 + Caderno cap 17 §17.4).
 *   - Caso contrário → renderiza badge com `mode` + `caucus`.
 *
 * Capability requerida: `ai-context-adversarial-filter` (Pro+).
 *
 * **Motor Neutro B4c-b:** `ConversationDetail.bond` deixou de ser stub `null` e passou a carregar
 * `adversarialMode`, então o badge é alcançável de fato. O gate deste hook **não mudou** — o que
 * mudou é que agora chega dado.
 */
export function useAdversarialIndicator(
  adversarial: AdversarialContext | null,
): AdversarialIndicatorResult {
  const hasCapability = useCapability('ai-context-adversarial-filter');

  if (adversarial === null) {
    return { isAdversarial: false, mode: null, caucus: null, hasCapability };
  }
  if (adversarial.adversarialMode === 'none') {
    return {
      isAdversarial: false,
      mode: null,
      caucus: adversarial.caucus,
      hasCapability,
    };
  }
  if (!hasCapability) {
    return {
      isAdversarial: false,
      mode: null,
      caucus: adversarial.caucus,
      hasCapability,
    };
  }
  return {
    isAdversarial: true,
    mode: adversarial.adversarialMode,
    caucus: adversarial.caucus,
    hasCapability,
  };
}
